import { Button } from "@/components/ui/button";
import { Home, Menu, X, Shield, User, LogOut, Settings, ChevronDown, MessageCircle } from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { NotificationCenter } from "@/components/NotificationCenter";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ContactSupportDialog } from "@/components/ContactSupportDialog";
import { Badge } from "@/components/ui/badge";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";
import { useTranslation } from 'react-i18next';
import tpsLogo from "@/assets/tps-logo.png";
import wbencLogo from "@/assets/wbenc-certified-logo.png";
import soc2Badge from "@/assets/soc2-badge.webp";
import { AdminLogger } from "@/lib/adminLogger";
export const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    t
  } = useTranslation();

  const scrollToSection = useCallback((sectionId: string) => {
    if (location.pathname !== "/") {
      navigate("/#" + sectionId);
    } else {
      const el = document.getElementById(sectionId);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      } else if (sectionId === "home") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
    setMobileMenuOpen(false);
  }, [location.pathname, navigate]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [user, setUser] = useState(null);
  const {
    unreadCount
  } = useUnreadMessages({
    isAdmin: false
  });
  useEffect(() => {
    // Check current session
    supabase.auth.getSession().then(({
      data: {
        session
      }
    }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        checkAdminStatus(session.user.id);
      }
    });

    // Listen for auth changes
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        setTimeout(() => {
          checkAdminStatus(session.user.id);
        }, 0);
      } else {
        setIsAdmin(false);
      }
    });
    return () => subscription.unsubscribe();
  }, []);
  const checkAdminStatus = async (userId: string) => {
    try {
      const {
        data
      } = await supabase.from("user_roles").select("role").eq("user_id", userId).eq("role", "admin").maybeSingle();
      setIsAdmin(!!data);
    } catch (error) {
      console.error("Error checking admin status:", error);
    }
  };
  const handleLogout = async () => {
    try {
      // Log the logout before signing out
      await AdminLogger.logout();
      const {
        error
      } = await supabase.auth.signOut();
      if (error) throw error;
      toast.success("Logged out successfully");
      navigate("/");
      setMobileMenuOpen(false);
    } catch (error: any) {
      console.error("Error logging out:", error);
      toast.error("Failed to log out");
    }
  };
  return <nav className="fixed top-0 left-0 right-0 z-50 bg-nav-bg border-b border-border" role="navigation" aria-label="Main navigation">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex-row flex items-center justify-start gap-3">
            <img alt="TPS Housing" onClick={() => navigate("/")} className="max-h-20 w-auto cursor-pointer shadow-none border-0 object-fill" src="/lovable-uploads/aa48f0d5-3e40-44e0-8eb9-ebee4850e4d8.jpg" width={80} height={80} />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-12">
            <button onClick={() => scrollToSection("home")} className="text-foreground hover:text-primary transition-colors font-['Helvetica_Neue',_Helvetica,_Arial,_sans-serif] pb-1 border-b-2 border-transparent hover:border-primary">
              {t('nav.home')}
            </button>
            <button onClick={() => scrollToSection("why-tps")} className="text-foreground hover:text-primary transition-colors font-['Helvetica_Neue',_Helvetica,_Arial,_sans-serif] pb-1 border-b-2 border-transparent hover:border-primary">
              {t('nav.whyTPS')}
            </button>
            <button onClick={() => scrollToSection("services")} className="text-foreground hover:text-primary transition-colors font-['Helvetica_Neue',_Helvetica,_Arial,_sans-serif] pb-1 border-b-2 border-transparent hover:border-primary">
              {t('nav.services')}
            </button>
            <button onClick={() => scrollToSection("testimonials")} className="text-foreground hover:text-primary transition-colors font-['Helvetica_Neue',_Helvetica,_Arial,_sans-serif] pb-1 border-b-2 border-transparent hover:border-primary">
              {t('nav.testimonials')}
            </button>
            <button onClick={() => scrollToSection("testimonials")} className="pb-1 border-b-2 border-transparent hover:border-primary transition-colors" title="Trust Center">
              <img src={soc2Badge} alt="SOC 2 Type 2 Compliance Certified - Trust Center" className="h-10 w-auto object-contain" width={40} height={40} />
            </button>
            <button onClick={() => scrollToSection("compliance")} className="pb-1 border-b-2 border-transparent hover:border-primary transition-colors" title="WBENC Certified">
              <img src={wbencLogo} alt="WBENC Certified Women's Business Enterprise" className="h-10 w-auto object-contain" width={72} height={40} />
            </button>
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <LanguageSwitcher />
            {isAdmin && <Button variant="outline" size="sm" onClick={() => navigate("/admin")}>
                <Shield className="w-4 h-4 mr-2" />
                Admin Portal
              </Button>}
            {user && <NotificationCenter />}
            {user ? <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <User className="w-4 h-4 mr-2" />
                    Account
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-background border border-border z-50">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">My Account</p>
                      <p className="text-xs leading-none text-muted-foreground">
                        {user.email}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/profile")} className="cursor-pointer">
                    <User className="mr-2 h-4 w-4" />
                    <span>Profile</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/dashboard")} className="cursor-pointer">
                    <Home className="mr-2 h-4 w-4" />
                    <span>View Dashboard</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/messages")} className="cursor-pointer">
                    <MessageCircle className="mr-2 h-4 w-4" />
                    <span>Messages</span>
                    {unreadCount > 0 && <Badge variant="destructive" className="ml-auto text-xs px-1.5 py-0">
                        {unreadCount > 9 ? "9+" : unreadCount}
                      </Badge>}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive">
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Logout</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu> : <Button size="sm" onClick={() => navigate("/auth")}>
                {t('nav.login')}
              </Button>}
          </div>

          {/* Mobile Menu Button */}
          <button className="lg:hidden text-foreground" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && <div className="lg:hidden py-4 border-t border-border">
            <div className="flex flex-col gap-4">
              <LanguageSwitcher />
              <button onClick={() => scrollToSection("home")} className="text-foreground hover:text-primary transition-colors text-left">
                {t('nav.home')}
              </button>
              <button onClick={() => scrollToSection("why-tps")} className="text-foreground hover:text-primary transition-colors text-left">
                {t('nav.whyTPS')}
              </button>
              <button onClick={() => scrollToSection("services")} className="text-foreground hover:text-primary transition-colors text-left">
                {t('nav.services')}
              </button>
              <button onClick={() => scrollToSection("testimonials")} className="text-foreground hover:text-primary transition-colors text-left">
                {t('nav.testimonials')}
              </button>
              <div className="flex flex-col gap-2 pt-2">
                {isAdmin && <Button variant="outline" size="sm" className="w-full" onClick={() => {
              navigate("/admin");
              setMobileMenuOpen(false);
            }}>
                    <Shield className="w-4 h-4 mr-2" />
                    Admin Portal
                  </Button>}
                {user ? <>
                    <div className="flex items-center justify-between px-2 py-1 border-b border-border">
                      <span className="text-xs text-muted-foreground">{user.email}</span>
                      <NotificationCenter />
                    </div>
                    <Button variant="outline" size="sm" className="w-full" onClick={() => {
                navigate("/profile");
                setMobileMenuOpen(false);
              }}>
                      <User className="w-4 h-4 mr-2" />
                      Profile
                    </Button>
                    <Button variant="outline" size="sm" className="w-full" onClick={() => {
                navigate("/messages");
                setMobileMenuOpen(false);
              }}>
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Messages
                    </Button>
                    <ContactSupportDialog trigger={<Button variant="secondary" size="sm" className="w-full">
                          <MessageCircle className="w-4 h-4 mr-2" />
                          Contact Support
                        </Button>} />
                    <Button size="sm" variant="destructive" className="w-full" onClick={handleLogout}>
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </Button>
                  </> : <Button size="sm" className="w-full" onClick={() => {
              navigate("/auth");
              setMobileMenuOpen(false);
            }}>
                    {t('nav.login')}
                  </Button>}
              </div>
            </div>
          </div>}
      </div>
    </nav>;
};